import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Header } from './../../Header/V2/Header'
import { Loading } from './../../Common/Loading'
import { GoToBack } from './../../Common/GoToBack'
import { Search } from './../../Common/Tables/Search'
import { LogErrors } from './../../../utilities/LogErrors'
import { ValidateSession } from './../../../utilities/BL/Sessions'
import { HELP_TYPE } from './../../../utilities/EL/HelpType'
import { WEBPAGES } from './../../../utilities/EL/WebPages'
import { TYPE_PROPOSITION_VALUE } from './../../../utilities/EL/TypePropositionValue'
import { ROLES } from './../../../utilities/EL/Roles'
import { BodyWithSideMenu } from './../../Dashboard/BodyWithSideMenu'


// Ayuda (Ztudiante)
// Armazón y lista basados en Instructor/Sections/V2/Help.js (validación de sesión +
// layout base + lista con buscador agrupada en categorías que se pueden
// contraer/expandir), adaptado al patrón que ya usan las páginas del Ztudiante
// (BodyWithSideMenu + Header directo, ver Results/V2/Results.js); los documentos son fijos.

// Manuales en PDF que se abren en una pestaña nueva, agrupados por categoría.
const helpCategories = [
    {
        title: 'Instalación',
        documents: [
            {
                text: 'Instalar ZtudiaApp v5.2 en Windows',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Manual%20Instalacion%20ZtudiaApp%20v5.2%20en%20Windows.pdf'
            },
            {
                text: 'Instalar ZtudiaApp v5.2 en macOS',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Manual%20Instalacion%20ZtudiaApp%20v5.2%20en%20MAC.pdf'
            },
            {
                text: 'Complemento de instalación y configuración de ZtudiaApp en macOs',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Gu%C3%ADa%20complementaria%20de%20instalaci%C3%B3n%20y%20configuraci%C3%B3n%20de%20ZtudiaApp%20en%20Mac.pdf'
            }
        ]
    },
    {
        title: 'Preguntas frecuentes',
        documents: [
            {
                text: 'Respuestas a preguntas frecuentes',
                url: 'https://documents.ztudia.com/MANUALES%20UX/Versi%C3%B3n%205.2/Preguntas%20frecuentes%20para%20Ztudiantes%20-%20Z.pdf'
            }
        ]
    }
]

const Help = () => {
    const navigate = useNavigate()

    const [theme, setTheme] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [searchText, setSearchText] = useState('')

    // Estado de contraído/expandido por categoría (por índice). Ausente/false = contraído;
    // true = expandido. Empiezan todas contraídas.
    const [openCategories, setOpenCategories] = useState({})

    const toggleCategory = (categoryIndex) => {
        setOpenCategories((prev) => ({
            ...prev,
            [categoryIndex]: prev[categoryIndex] !== true
        }))
    }

    // Filtra los documentos de cada categoría según la búsqueda, y descarta las
    // categorías que se quedan sin resultados.
    const filteredCategories = useMemo(() => {
        const search = (searchText ?? '').trim().toLowerCase()

        return helpCategories
            .map((category) => ({
                ...category,
                documents: search
                    ? category.documents.filter((document) => document.text.toLowerCase().includes(search))
                    : category.documents
            }))
            .filter((category) => category.documents.length > 0)
    }, [searchText])

    const handleSearch = (value) => {
        setSearchText(value)
    }

    /**
     * Función para obtener la configuración, una vez que termine de cargarse el componente
     */
    useEffect(() => {
        async function Validate() {
            try {
                const tmpSession = await ValidateSession()

                if (tmpSession.success) {
                    if (tmpSession.data.user.userStatus === 2) {
                        setTheme(tmpSession.data.user.userTheme)
                        document.body.classList.add('BodyComplete')
                        setIsLoading(false)
                    } else {
                        navigate('/confirm')
                    }
                } else {
                    navigate(`/?logout=${process.env.REACT_APP_LOGOUT_PARAMETER}`)
                }
            } catch (error) {
                await LogErrors('Validate - Help', error)
            }
        }
        Validate()
    }, []) // eslint-disable-line

    return (
        <>
            {isLoading ? (
                <Loading />
            ) : (
                <BodyWithSideMenu page={WEBPAGES.ZTUDENT.HELP} theme={theme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} role={ROLES.ZTUDENT}>
                    <Header theme={theme} setTheme={setTheme} propositionValue={TYPE_PROPOSITION_VALUE.ZTUDENT} idHelp={HELP_TYPE.ZTUDENT.DASHBOARD} />

                    <main className='container Instructor__Help Container-FullHeight'>
                        <GoToBack url={'/dashboard'} />

                        <p className='Instructor__Title Instructor__Title--Center Instructor__SpaceUp'>Ayuda</p>

                        <Search onChange={handleSearch} extraClassName={'Instructor__Syllabus__Search'} />

                        <div>
                            {filteredCategories.length === 0 ? (
                                <p className='Instructor__Text--Center Instructor__SpaceUp'>No se encontró algún documento con la búsqueda realizada. Intenta con otra.</p>
                            ) : (
                                filteredCategories.map((category) => {
                                    // Mientras se busca, se mantienen expandidas las categorías con resultados
                                    // (sin importar su estado manual), para que el usuario vea lo que encontró.
                                    const categoryIndex = helpCategories.findIndex((c) => c.title === category.title)
                                    const isOpen = searchText.trim() !== '' || openCategories[categoryIndex] === true

                                    return (
                                        <div key={category.title} className={`Blocks Instructor__Help__Category ${isOpen ? 'Blocks-Show' : ''}`}>
                                            <div className='Block' onClick={() => toggleCategory(categoryIndex)}>
                                                <div className='Block-Info'>
                                                    <p className='Block-Title'>{category.title}</p>
                                                </div>

                                                <div className='Block-Buttons'>
                                                    <button type='button' className='Block-Buttons-List' onClick={(e) => { e.stopPropagation(); toggleCategory(categoryIndex) }}>
                                                        <i className={`bi bi-caret-${isOpen ? 'up' : 'down'}-fill`}></i>
                                                    </button>
                                                </div>
                                            </div>

                                            {isOpen && (
                                                category.documents.map((document, index) => (
                                                    <div key={index} className='Instructor__Syllabus__CoursesList'>
                                                        <button type='button' className='Instructor__Syllabus__CoursesList__Items Tables--Shadow' onClick={() => window.open(document.url, '_blank', 'noopener,noreferrer')}>
                                                            <span className='Instructor__Syllabus__CoursesList__Items__Text'>{document.text}</span>
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )
                                })
                            )}
                        </div>
                    </main>
                </BodyWithSideMenu>
            )}
        </>
    )
}

export { Help };
